import { useEffect, useState, useRef } from "react";
import { Link } from "react-router-dom";
import { UserHomepageSectionsService } from "../services/UserHomepageSectionsService";

// Hardcoded defaults — identical to the original text.
// These render immediately so there is zero layout shift on first paint.
const DEFAULT_TITLE = "Legacy in Motion";
const DEFAULT_BODY =
  "<p>Temidayo Balogun is a pan-African artist, composer, producer, and multi-instrumentalist whose work is deeply rooted in African music, jazz, and contemporary sounds. Originally from Ejigbo, Osun State, Nigeria, he is proficient on the saxophone, piano, flute, clarinet, and talking drum.</p>" +
  "<p>His musical journey began with formal training at the Peter King College of Music and the MUSON School of Music in Lagos, before advancing to the Longy School of Music in Cambridge, Massachusetts. There, he refined his artistry under the mentorship of respected musicians including Noah Preminger, Eric Hofbauer, and Ben Schwendener.</p>" +
  "<p>Temidayo's career spans dynamic solo work and wide-ranging collaborations across Nigeria and the United States. He has worked with artists such as Yinka Davies, Taye Mayegha, and Tosin Aribisala, as well as ensembles including The Lagos Jazz Series Quintet, Generayshun, The Makanda Project, and Charlie Kohlhase's Saxophone Support Group.</p>";

const SkeletonLine = ({ className = "" }) => (
  <div className={`animate-pulse bg-black/[0.08] rounded ${className}`} />
);

export default function JazzJourney() {
  const [isVisible, setIsVisible] = useState(false);
  const sectionRef = useRef(null);

  // Section content — pre-seeded with defaults to avoid layout shift
  const [sectionData, setSectionData] = useState({
    section_title: DEFAULT_TITLE,
    section_body: DEFAULT_BODY,
  });
  const [isContentLoading, setIsContentLoading] = useState(true);

  // Intersection Observer for entrance animation (unchanged)
  useEffect(() => {
    const section = sectionRef.current;
    if (!section) return;

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
        }
      },
      { threshold: 0.15 },
    );

    observer.observe(section);

    return () => {
      observer.unobserve(section);
    };
  }, []);

  // Fetch dynamic section content
  useEffect(() => {
    const fetchSectionData = async () => {
      try {
        const response = await UserHomepageSectionsService.getSections();
        if (response.status === "success" && response.data?.legacy_in_motion) {
          const { section_title, section_body } = response.data.legacy_in_motion;
          setSectionData({
            section_title: section_title || DEFAULT_TITLE,
            section_body: section_body || DEFAULT_BODY,
          });
        }
      } catch {
        // Silently fall back to defaults — no visible error for the visitor
      } finally {
        setIsContentLoading(false);
      }
    };

    fetchSectionData();
  }, []);

  return (
    <section
      ref={sectionRef}
      id="biography"
      className="py-16 sm:py-24 lg:py-32 px-4 sm:px-6 bg-white overflow-hidden"
      style={{
        fontFamily: "Crimson Pro, Georgia, serif",
      }}
    >
      <div className="max-w-[1200px] mx-auto">
        {/* Section with rotated image cards and text */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 sm:gap-16 lg:gap-24 items-center">
          {/* Left - Image composition (unchanged) */}
          <div
            className={`relative min-h-[400px] sm:min-h-[500px] lg:h-[600px] transition-all duration-1000 ${isVisible
              ? "opacity-100 translate-x-0"
              : "opacity-0 -translate-x-12"
              }`}
          >
            {/* Back image - stacked on mobile, rotated on desktop */}
            <div className="biography-image-back relative sm:absolute top-0 sm:top-8 left-0 w-full sm:w-[280px] md:w-[340px] max-w-[340px] mx-auto sm:mx-0 mb-6 sm:mb-0 bg-white border border-black/10 p-4 pb-12 shadow-[0_8px_30px_rgba(0,0,0,0.12)] transition-all duration-500 hover:-translate-y-2 hover:shadow-[0_12px_40px_rgba(0,0,0,0.18)]">
              <div className="w-full h-[280px] md:h-[340px] bg-black/5 overflow-hidden">
                <img
                  src="/static/images/jazz_journey_1.JPG"
                  alt="Jazz performance on stage"
                  className="w-full h-full object-cover"
                />
              </div>
              <p
                className="text-center mt-4 text-black/70 text-sm"
                style={{ fontFamily: "Gloria Hallelujah, cursive" }}
              >
                Live Performance Recording
              </p>
            </div>

            {/* Front image - stacked on mobile, rotated on desktop */}
            <div className="biography-image-front relative sm:absolute bottom-0 right-0 w-full sm:w-[280px] md:w-[340px] max-w-[340px] mx-auto sm:mx-0 bg-white border border-black/10 p-4 pb-12 shadow-[0_8px_30px_rgba(0,0,0,0.12)] transition-all duration-500 hover:-translate-y-2 hover:shadow-[0_12px_40px_rgba(0,0,0,0.18)] z-10">
              <div className="w-full h-[280px] md:h-[340px] bg-black/5 overflow-hidden">
                <img
                  src="/static/images/jazz_journey_2.JPG"
                  alt="Close-up of hands playing piano keys"
                  className="w-full h-full object-cover"
                />
              </div>
              <p
                className="text-center mt-4 text-black/70 text-sm"
                style={{ fontFamily: "Gloria Hallelujah, cursive" }}
              >
                The art of improvisation ♪
              </p>
            </div>
          </div>

          {/* Right - Dynamic text content */}
          <div
            className={`space-y-6 sm:space-y-8 transition-all duration-1000 delay-300 ${isVisible
              ? "opacity-100 translate-x-0"
              : "opacity-0 translate-x-12"
              }`}
          >
            <div>
              {/* Dynamic section title */}
              {isContentLoading ? (
                <SkeletonLine className="h-12 w-3/4 mb-4" />
              ) : (
                <h2
                  className="text-3xl sm:text-4xl lg:text-6xl font-normal text-black mb-4 sm:mb-6"
                  style={{
                    fontFamily: "Playfair Display, Georgia, serif",
                    fontWeight: 400,
                    lineHeight: "1.1",
                    letterSpacing: "-0.01em",
                  }}
                >
                  {sectionData.section_title}
                </h2>
              )}
              <div className="w-16 h-[2px] bg-black mb-6 sm:mb-8" />
            </div>

            {/* Dynamic rich-text body */}
            {isContentLoading ? (
              <div className="space-y-4">
                <SkeletonLine className="h-4 w-full" />
                <SkeletonLine className="h-4 w-full" />
                <SkeletonLine className="h-4 w-5/6" />
                <SkeletonLine className="h-4 w-full" />
                <SkeletonLine className="h-4 w-full" />
                <SkeletonLine className="h-4 w-4/5" />
                <SkeletonLine className="h-4 w-full" />
                <SkeletonLine className="h-4 w-3/4" />
              </div>
            ) : (
              <div
                className="jazz-journey-body w-full max-w-full overflow-hidden break-words text-base sm:text-lg text-black"
                style={{
                  fontFamily: "Crimson Pro, Georgia, serif",
                  lineHeight: "1.8",
                }}
                dangerouslySetInnerHTML={{
                  __html: (sectionData.section_body || "")
                    .replace(/&nbsp;/g, " ")
                    .replace(/\u00a0/g, " "),
                }}
              />
            )}

            <div className="pt-2 sm:pt-4">
              <Link
                to="/biography"
                className="inline-block border-b-2 border-black text-black font-medium hover:border-black/40 hover:text-black/60 transition-all duration-300 touch-manipulation py-1"
                style={{
                  fontFamily: "Montserrat, sans-serif",
                  fontSize: "14px",
                  letterSpacing: "0.5px",
                }}
              >
                READ FULL BIOGRAPHY
              </Link>
            </div>
          </div>
        </div>
      </div>

      {/* Apply rotations only on sm+ screens */}
      <style jsx="true">{`
        @media (min-width: 640px) {
          .biography-image-back {
            transform: rotate(-8deg);
          }
          .biography-image-back:hover {
            transform: rotate(-6deg) translateY(-8px);
          }
          .biography-image-front {
            transform: rotate(5deg);
          }
          .biography-image-front:hover {
            transform: rotate(8deg) translateY(-8px);
          }
        }
        
        @media (max-width: 639px) {
          .biography-image-back:hover,
          .biography-image-front:hover {
            transform: translateY(-8px);
          }
        }
      `}</style>
    </section>
  );
}
